import { useState, useEffect } from "react";

// Locatie per event — Almeerderstrand, Almere (IJmeerdijk 4), Pal Mundo 2026.
const EVENT_LOCATION = { lat: 52.34, lon: 5.152 } as const;

/** WMO-weercodes die onweer aangeven (KNMI-seamless nomenclatuur). */
const THUNDER_CODES: ReadonlySet<number> = new Set([95, 96, 99]);

/**
 * Drempelwaarden windstoten (Beaufort) — Veiligheidsplan PM26 V4 §15.13.3:
 * verwachte Bf 6 = CMT-overleg (oranje) · constructies gegarandeerd tót Bf 7
 * (rood = grens bereikt) · verwachte Bf 8 = buitengebied met tentconstructies
 * ontruimen. Geel (Bf 5) is vroegsignalering van de CP, geen planwaarde.
 */
const DREMPEL_GEEL = 5;
const DREMPEL_ORANJE = 6;
const DREMPEL_ROOD = 7;

/** EMS-weersituatieschaal PM26 (Medisch plan v3.1 §3): situatie 1 vanaf 25°C
 * (extra water/ijs/schaduw); situatie 2 boven 32°C (personeel + gekoelde unit + GHOR). */
const HITTE_GRENS_C = 25;

/** Beaufort-schaal op basis van windsnelheid in km/h. */
export function beaufortFromKmh(kmh: number): number {
  if (kmh < 1) return 0;
  if (kmh < 6) return 1;
  if (kmh < 12) return 2;
  if (kmh < 20) return 3;
  if (kmh < 29) return 4;
  if (kmh < 39) return 5;
  if (kmh < 50) return 6;
  if (kmh < 62) return 7;
  if (kmh < 75) return 8;
  if (kmh < 89) return 9;
  if (kmh < 103) return 10;
  if (kmh < 118) return 11;
  return 12;
}

export interface WeatherCurrent {
  tempC: number;
  windSpeedKmh: number;
  windGustsKmh: number;
  precipitationMm: number;
  weatherCode: number;
}

export interface ForecastHour {
  time: string;              // "HH:mm" lokale tijd
  windGustsKmh: number;
  precipitationProb: number; // 0–100
  beaufort: number;
}

export type WeatherStatus = "ok" | "geel" | "oranje" | "rood" | "onbekend";

export interface WeatherState {
  current: WeatherCurrent;
  forecast6h: ForecastHour[];
  status: WeatherStatus;
  hitte: boolean;
  laatstOpgehaald: Date;
}

interface OpenMeteoResponse {
  current: {
    temperature_2m: number;
    wind_speed_10m: number;
    wind_gusts_10m: number;
    precipitation: number;
    weather_code: number;
  };
  hourly: {
    time: string[];
    wind_gusts_10m: number[];
    precipitation_probability: (number | null)[];
  };
}

/**
 * Haalt actueel weer op via open-meteo (KNMI seamless model, geen API-key).
 * Standaard locatie: EVENT_LOCATION — pas aan per event.
 */
export async function fetchWeather(
  lat: number = EVENT_LOCATION.lat,
  lon: number = EVENT_LOCATION.lon,
): Promise<WeatherState> {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current:
      "temperature_2m,wind_speed_10m,wind_gusts_10m,precipitation,weather_code",
    hourly: "wind_gusts_10m,precipitation_probability",
    wind_speed_unit: "kmh",
    models: "knmi_seamless",
    timezone: "auto",
    forecast_days: "1",
  });

  const res = await fetch(
    `https://api.open-meteo.com/v1/forecast?${params.toString()}`,
  );
  if (!res.ok) throw new Error(`open-meteo HTTP ${res.status}`);
  const json = (await res.json()) as OpenMeteoResponse;

  const current: WeatherCurrent = {
    tempC: json.current.temperature_2m,
    windSpeedKmh: json.current.wind_speed_10m,
    windGustsKmh: json.current.wind_gusts_10m,
    precipitationMm: json.current.precipitation,
    weatherCode: json.current.weather_code,
  };

  // Zoek het huidige uur in de hourly-reeks.
  // open-meteo geeft lokale tijden terug bij timezone=auto ("YYYY-MM-DDT14:00").
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const currentHourKey = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:00`;
  const foundIdx = json.hourly.time.indexOf(currentHourKey);
  const startIdx = foundIdx >= 0 ? foundIdx : 0;

  const forecast6h: ForecastHour[] = json.hourly.time
    .slice(startIdx, startIdx + 6)
    .map((t, i) => {
      const absIdx = startIdx + i;
      const gustsKmh = json.hourly.wind_gusts_10m[absIdx] ?? 0;
      return {
        time: t.slice(11, 16), // "HH:mm"
        windGustsKmh: gustsKmh,
        precipitationProb: json.hourly.precipitation_probability[absIdx] ?? 0,
        beaufort: beaufortFromKmh(gustsKmh),
      };
    });

  // Status op basis van windstoten + onweer-indicatie
  const gustsBf = beaufortFromKmh(current.windGustsKmh);
  const onweer = THUNDER_CODES.has(current.weatherCode);

  let status: WeatherStatus = "ok";
  if (gustsBf >= DREMPEL_ROOD || onweer) status = "rood";
  else if (gustsBf >= DREMPEL_ORANJE) status = "oranje";
  else if (gustsBf >= DREMPEL_GEEL) status = "geel";

  return {
    current,
    forecast6h,
    status,
    hitte: current.tempC >= HITTE_GRENS_C,
    laatstOpgehaald: new Date(),
  };
}

const POLL_INTERVAL_MS = 15 * 60 * 1_000; // 15 minuten

/**
 * Hook die het weer elke 15 minuten ophaalt.
 * Bij een fetch-fout wordt de vorige meting behouden met status 'onbekend'.
 * Geeft null terug tot de eerste fetch slaagt.
 */
export function useWeather(): WeatherState | null {
  const [state, setState] = useState<WeatherState | null>(null);

  useEffect(() => {
    let mounted = true;
    let prev: WeatherState | null = null;

    async function poll(): Promise<void> {
      try {
        const data = await fetchWeather();
        if (!mounted) return;
        prev = data;
        setState(data);
      } catch {
        if (!mounted) return;
        if (prev !== null) setState({ ...prev, status: "onbekend" });
      }
    }

    void poll();
    const id = setInterval(() => void poll(), POLL_INTERVAL_MS);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  return state;
}
